import React, { useEffect, useState } from "react";
import { Form, Input, Button, Select, Card, Space, Divider } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver"
import JSZip from "jszip";
import { SERVICES } from "./constants";
import { formatDate } from "./utils";
import { createChecklist, getChecklist } from "./api";
import { useNavigate, useParams } from "react-router-dom";

const { Option } = Select;

type ServiceType = {
  serviceId: number;
  mf_name: string;
  microfront: {
    mf_version: string,
    mf_uniq_number: string,
    mf_prev_version: string,
  },
  hcms?: {
    mf_locale_name: string;
    hcmsContent?: string;
  };
}

type FormValues = {
  services: ServiceType[];
  commonCors?: { name: string; value: string }[];
  title?: string;
}



const App: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [showCommonCors, setShowCommonCors] = useState(false);
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  useEffect(() => {
    if (params.id) {
      setPrefillLoading(true);
      getChecklist(params.id)
        .then((rec) => {
          if (rec?.formData) {
            form.setFieldsValue(rec.formData);
            setShowCommonCors(Boolean(rec.formData.commonCors?.length));
          }
        })
        .finally(() => setPrefillLoading(false));
    } else {
      form.resetFields();
      setShowCommonCors(false);
    }
  }, [params.id]);

  const onServiceChange = (serviceId: number, index: number) => {
    const service = SERVICES.find((s) => s.id === serviceId);
    if (service) {
      const services = form.getFieldValue("services") || [] as ServiceType[];
      if (services[index]) {
        services[index].mf_name = service.mf_name;
      }
      if (services[index]?.hcms) {
        services[index].hcms.mf_locale_name = service.mf_locale_name;
      }
      form.setFieldsValue({ services });
    }
  };

  const addMicrofrontBlock = (index: number) => {
    const services = form.getFieldValue("services") || [];
    if (!services[index]) return;

    if (!services[index].microfront) {
      services[index].microfront = {
        mf_version: "",
        mf_uniq_number: "",
        mf_prev_version: "",
      };
      form.setFieldsValue({ services });
    }
  };

  // Добавить блок HCMS внутри сервиса
  const addHcmsBlock = (index: number) => {
    const services = form.getFieldValue("services") || [];
    if (!services[index]) return;

    if (!services[index].hcms) {
      const serviceId = services[index].serviceId;
      const service = SERVICES.find((s) => s.id === serviceId);
      if (service?.mf_locale_name) {
        services[index].hcms = {
          mf_locale_name: service.mf_locale_name,
          hcmsContent: "",
        };
        form.setFieldsValue({ services });
      }
    }
  };

  // Удалить блоки CORS и HCMS
  const removeMicrofrontBlock = (index: number) => {
    const services = form.getFieldValue("services") || [];
    if (!services[index]) return;
    services[index].microfront = undefined;
    form.setFieldsValue({ services });
  };
  const removeHcmsBlock = (index: number) => {
    const services = form.getFieldValue("services") || [];
    if (!services[index]) return;
    services[index].hcms = undefined;
    form.setFieldsValue({ services });
  };

  const parseHcmsTsToObject = (code: string) => {
    const cleaned = code
      .trim()
      .replace(/^\s*export\s+default\s+/, "")
      .replace(/as\s+const;?\s*$/i, "");

    // eslint-disable-next-line no-new-func
    const fn = new Function(`return (${cleaned});`);
    return fn();
  };

  const generateHcmsArchive = async (values: any) => {
    const services: ServiceType[] = values.services || [];
    const zip = new JSZip();
    const folder = zip.folder("hcms");

    if (!folder) return;

    for (const service of services) {
      const localeName = service.hcms?.mf_locale_name;
      const content = service.hcms?.hcmsContent;

      if (!content) return;

      try {
        const tsObj = parseHcmsTsToObject(content);
        const json = JSON.stringify({ locale: tsObj }, null, 2);
        const fileName = localeName?.endsWith(".json") ? localeName : `${localeName}.json`;
        folder.file(fileName, json);
      } catch (e) {
        console.error("Ошибка парсинга HCMS TS-контента для сервиса", service.serviceId, e);
      }
    }

    const hasFiles = Object.keys(zip.files).length > 0;
    if (!hasFiles) return;

    const out = await zip.generateAsync({ type: "blob" });
    const fileName = `${values.title || `HCMS`}_${formatDate(new Date())}.zip`;
    saveAs(out, fileName);
  };

  // Генерация docx
  const generateDocx = async (values: any) => {
    setLoading(true);
    try {
      // Загружаем шаблон (template.docx должен лежать в public)
      const response = await fetch("/template.docx");
      const arrayBuffer = await response.arrayBuffer();

      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });

      const flattenServices = values.services?.map((service: ServiceType) => ({ ...service, ...service?.microfront ?? {}, ...service?.hcms ?? {} }));
      const hasCommonCORS = Array.isArray(values.commonCors) && values.commonCors.length > 0;
      const hasMicrofront = values.services?.some((s: ServiceType) => s?.microfront);

      if (!hasMicrofront) return;

      doc.render({
        hasCommonCORS,
        hasMicrofront,
        hasHCMS: values.services?.some((s: ServiceType) => s?.hcms),
        hasCORS: hasCommonCORS || hasMicrofront,
        services: flattenServices,
        commonCors: values.commonCors || [],
      });
      const out = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const fileName = `${values.title || `Чеклист_МФ}`}_${formatDate(new Date())}.docx`

      saveAs(out, fileName);
    } catch (e) {
      console.error("Ошибка генерации документа:", e);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    // Сохраняем запись в БД перед генерацией
    const author = localStorage.getItem("fullName") || localStorage.getItem("username") || "unknown";
    const title = values.title || `Чеклист_МФ_${formatDate(new Date())}`;
    try {
      await createChecklist({ formData: values, author, title });
    } catch (e) {
      // Логируем, но не блокируем генерацию файла
      console.error("Ошибка сохранения чеклиста:", e);
    }
    await generateDocx(values);
    await generateHcmsArchive(values);
    navigate('/');
  };

  return (
    <div style={{ maxWidth: 800, margin: "auto", padding: 20 }}>
      <Form
        form={form}
        name="dynamic_services_form"
        onFinish={onFinish}
        autoComplete="off"
        layout="vertical"
      >
        <Card size="small" style={{ marginBottom: 20 }}>
          <Form.Item
            name="title"
            label="Наименование чеклиста"
          >
            <Input placeholder="Введите наименование чеклиста" />
          </Form.Item>
        </Card>
        {!showCommonCors && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => {
              setShowCommonCors(true);
              if (!form.getFieldValue("commonCors")?.length) {
                form.setFieldsValue({ commonCors: [{ name: "", value: "msa" }] });
              }
            }}
            style={{ width: "100%", marginBottom: 20 }}
          >
            Добавить общие CORS
          </Button>
        )}
        {showCommonCors && (
          <Card
            size="small"
            title="Общие CORS"
            style={{ marginBottom: 20 }}
            extra={
              <MinusCircleOutlined
                onClick={() => {
                  setShowCommonCors(false);
                  form.setFieldsValue({ commonCors: [] });
                }}
              />
            }
          >
            <Form.List name="commonCors">
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        rules={[
                          { required: true, message: 'Обязательное поле' },
                          { pattern: /^[A-Za-z0-9_.\-]+$/, message: 'Только английские символы, цифры, _ . -' },
                        ]}
                        style={{ marginBottom: 0, minWidth: 320 }}
                      >
                        <Input placeholder="Наименование параметра" />
                      </Form.Item>
                      <Form.Item
                        name={[name, 'value']}
                        initialValue="msa"
                        style={{ marginBottom: 0 }}
                      >
                        <Input disabled style={{ width: 120 }} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.ErrorList errors={errors} />
                  <Form.Item style={{ marginTop: 8 }}>
                    <Button type="dashed" onClick={() => add({ name: '', value: 'msa' })} icon={<PlusOutlined />}>Добавить параметр</Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>
        )}
        <Form.List name="services">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card
                  key={key}
                  size="small"
                  title={`Сервис #${name + 1}`}
                  style={{ marginBottom: 20 }}
                  extra={<MinusCircleOutlined onClick={() => remove(name)} />}
                >
                  <Form.Item
                    {...restField}
                    name={[name, "serviceId"]}
                    label="Выберите микрофронт"
                    rules={[{ required: true, message: "Выберите микрофронт" }]}
                  >
                    <Select
                      placeholder="Выберите сервис"
                      onChange={(value) => onServiceChange(value, name)}
                    >
                      {SERVICES.map((s) => (
                        <Option key={s.id} value={s.id}>
                          {s.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "mf_name"]}
                    label="Наименование микрофронта"
                  >
                    <Input disabled />
                  </Form.Item>

                  <>
                    {
                      (() => {
                        const currentServiceId = form.getFieldValue(["services", name, "serviceId"]);
                        const svc = SERVICES.find((s) => s.id === currentServiceId);
                        const hasLocale = !!svc?.mf_locale_name;
                        const hasMicrofrontName = !!svc?.mf_name;

                        return (
                          <Space size="middle" style={{ marginBottom: 16 }}>
                            {hasMicrofrontName ? <Button
                              type="dashed"
                              disabled={!!form.getFieldValue(["services", name, "microfront"])}
                              onClick={() => addMicrofrontBlock(name)}
                            >
                              Добавить версии для установки МФ
                            </Button> : null}
                            {hasLocale ? <Button
                              type="dashed"
                              disabled={!!form.getFieldValue(["services", name, "hcms"])}
                              onClick={() => addHcmsBlock(name)}
                            >
                              Добавить HCMS
                            </Button> : null}
                          </Space>
                        );
                      })()
                    }
                  </>

                  {/* Блок микрофронта */}
                  {form.getFieldValue(["services", name, "microfront"]) && (
                    <Card
                      size="small"
                      type="inner"
                      title="Версии для установки"
                      extra={
                        <MinusCircleOutlined onClick={() => removeMicrofrontBlock(name)} />
                      }
                      style={{ marginBottom: 16 }}
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "microfront", "mf_version"]}
                        label="Версия МФ для установки"
                        rules={[
                          { required: true, message: "Введите версию" },
                          { pattern: /^\d{1,3}\.\d{1,3}\.\d{1,3}$/, message: "Формат: 111.222.333" },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "microfront", "mf_uniq_number"]}
                        label="Уникальный номер сборки (1.22.33-20250901123344)"
                        rules={[
                          { required: true, message: "Введите уникальный номер сборки" },
                          { pattern: /^\d{1,3}\.\d{1,3}\.\d{1,3}-\d{14}$/, message: "Полный номер с версией и уникальным кодом (1.2.3-20250901123344)" },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "microfront", "mf_prev_version"]}
                        label="Предыдущая версия МФ (для отката)" rules={[
                          { required: true, message: "Введите предыдущую версию" },
                          { pattern: /^\d{1,3}\.\d{1,3}\.\d{1,3}$/, message: "Формат: 111.222.333" },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Card>
                  )}

                  {/* Блок HCMS */}
                  {form.getFieldValue(["services", name, "hcms"]) && (
                    <Card
                      size="small"
                      type="inner"
                      title="HCMS"
                      extra={
                        <MinusCircleOutlined onClick={() => removeHcmsBlock(name)} />
                      }
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "hcms", "mf_locale_name"]}
                        label="Наименование файла локалей"
                      >
                        <Input disabled />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "hcms", "hcmsContent"]}
                        label="HCMS content (TS)"
                        rules={[{ required: true, message: "Заполните контент HCMS" }]}
                      >
                        <Input.TextArea
                          rows={8}
                          placeholder={`export default {\n  shimmer: {\n    loaderAriaText: 'Загрузка',\n    breadcrumbTitle: 'Назад',\n  },\n  // ...\n} as const;`}
                        />
                      </Form.Item>
                    </Card>
                  )}
                </Card>
              ))}

              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Добавить микрофронт
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading || prefillLoading} block>
            Сгенерировать чеклист
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default App;
